import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { 
  getLivestreamById, 
  getLivestreamByIdPublic, 
  joinLivestream, 
  stopLivestream,
  deleteLivestream,
  createLivestream,
  createCheckoutSession,
  isJoinResponseGranted,
  isJoinResponsePaymentRequired,
  getJoinResponseLivestream,
  getJoinResponseSessionUrl,
  getJoinResponseTag,
  parseJoinLivestreamResponse
} from '../utils/livestreamApi';
import { handleLivestreamError, LIVESTREAM_ERRORS } from '../utils/livestreamErrorHandler';
import { fetchLivestreams } from '../store/LivestreamSlice';
import livestreamScheduleService from '../services/livestreamScheduleService';

export const useLivestreamManagement = (livestreamId, user, initialLivestream = null) => {
  const dispatch = useDispatch();
  const [livestream, setLivestream] = useState(initialLivestream);
  const [isLoading, setIsLoading] = useState(!initialLivestream);
  const [error, setError] = useState(null);
  const [isJoining, setIsJoining] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);

  // Error message mapping - memoized to prevent dependency issues
  const ERROR_MESSAGES = useMemo(() => ({
    LIVESTREAM_NOT_FOUND: 'Livestream not found',
    LIVESTREAM_UNAUTHORIZED_ACCESS: 'You are not authorized to access this livestream',
    LIVESTREAM_FORBIDDEN: 'You do not have permission to access this livestream',
    LIVESTREAM_PAYMENT_REQUIRED: 'Payment is required to watch this livestream',
    LIVESTREAM_NOT_IN_PROGRESS: 'This livestream is not in progress yet',
    LIVESTREAM_ALREADY_COMPLETED: 'This livestream has already ended',
    LIVESTREAM_INVALID_STATUS: 'Invalid livestream status',
    UNAUTHORIZED: 'Please log in to access this livestream',
    USER_NOT_FOUND: 'User not found',
    COURT_NOT_FOUND: 'Court not found',
    CAMERA_NOT_FOUND: 'Camera not found',
    LIVESTREAM_WATCH_URL_MISSING: 'Stream is not available yet',
    LIVESTREAM_EXPIRED: 'This livestream has expired',
    LIVESTREAM_ALREADY_CANCELLED: 'This livestream has been cancelled',
    LIVESTREAM_ALREADY_IN_PROGRESS: 'This livestream is already in progress',
    VIEWER_ALREADY_JOINED: 'You have already joined this livestream',
    LIVESTREAM_VIEW_LIMIT_REACHED: 'Viewer limit reached for this livestream',
    LIVESTREAM_START_AFTER_END: 'Cannot start livestream after end time',
    LIVESTREAM_DESCRIPTION_EMPTY: 'Livestream description is required',
    LIVESTREAM_LOGO_EMPTY: 'Livestream logo is required',
    LIVESTREAM_SPONSORS_EMPTY: 'At least one sponsor is required',
    LIVESTREAM_PRICE_NEGATIVE: 'Livestream price cannot be negative',
    LIVESTREAM_UPDATE_NULL: 'No updates provided',
    TEAM_INFO_MISSING: 'Team information is missing',
    COURT_OR_OWNER_NULL: 'Court or owner information is missing'
  }), []);

  const fetchLivestream = useCallback(async (forceRefresh = false) => {
    if (!livestreamId) return;

    // Skip fetch if we already have data and it's not a forced refresh
    if (!forceRefresh && livestream && !isLoading) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      let response;
      if (user) {
        // Authenticated user - try private endpoint first
        try {
          response = await getLivestreamById(livestreamId);
        } catch (error) {
          // If private endpoint fails, try public endpoint
          response = await getLivestreamByIdPublic(livestreamId);
        }
      } else {
        // Unauthenticated user - use public endpoint
        response = await getLivestreamByIdPublic(livestreamId);
      }

      const livestreamData = response.data?.data || response.data;
      
      console.log('[useLivestreamManagement] fetchLivestream - Received livestream data:', livestreamData);
      console.log('[useLivestreamManagement] Livestream object details:', {
        id: livestreamData.id,
        description: livestreamData.description,
        status: livestreamData.status,
        startTime: livestreamData.startTime,
        endTime: livestreamData.endTime,
        price: livestreamData.price,
        requiresPayment: livestreamData.requiresPayment,
        courtId: livestreamData.courtId,
        sponsors: livestreamData.sponsors,
        teamAName: livestreamData.teamAName,
        teamBName: livestreamData.teamBName
      });
      
      // Only update if data has actually changed to prevent unnecessary re-renders
      if (!livestream || JSON.stringify(livestream) !== JSON.stringify(livestreamData)) {
        setLivestream(livestreamData);
        
        // Update Redux store with the fetched livestream
        dispatch(fetchLivestreams());
      }
    } catch (err) {
      const errorMessage = handleLivestreamError(err, ERROR_MESSAGES);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [livestreamId, user, livestream, isLoading, dispatch, ERROR_MESSAGES]);

  const handleJoinStream = useCallback(async (purchaseDto = null) => {
    if (!livestreamId || !user) {
      toast.error('Please log in to join the stream');
      return;
    }

    try {
      setIsJoining(true);
      const response = await joinLivestream(livestreamId, purchaseDto);
      const parsedResponse = parseJoinLivestreamResponse(response);
      
      console.log('🔍 useLivestreamManagement - Parsed Join Response:', parsedResponse);
      
      if (parsedResponse.isGranted) {
        const livestream = parsedResponse.livestream;
        const tag = parsedResponse.tag;
        
        toast.success(`Successfully joined the stream! (${tag})`);
        
        // Update local livestream state if we got updated livestream data
        if (livestream) {
          setLivestream(livestream);
        } else {
          // Refresh livestream data to update viewer list (force refresh)
          await fetchLivestream(true);
        }
      } else if (parsedResponse.isPaymentRequired) {
        const sessionUrl = parsedResponse.sessionUrl;
        if (sessionUrl) {
          // Open payment session in new tab
          window.open(sessionUrl, '_blank', 'noopener');
          toast.info('Payment required. Please complete payment to join the stream.');
        } else {
          toast.error('Payment required but no payment URL provided.');
        }
      } else {
        toast.error('Unable to join stream. Please try again.');
      }
    } catch (err) {
      const errorMessage = handleLivestreamError(err, ERROR_MESSAGES);
      toast.error(errorMessage);
    } finally {
      setIsJoining(false);
    }
  }, [livestreamId, user, fetchLivestream, ERROR_MESSAGES]);

  const handleStopStream = useCallback(async () => {
    if (!livestreamId) return;

    try {
      setIsStopping(true);
      await stopLivestream(livestreamId);
      toast.success('Livestream stopped successfully');
      
      // Refresh livestream data
      await fetchLivestream();
      // Refresh livestreams list
      dispatch(fetchLivestreams());
    } catch (err) {
      const errorMessage = handleLivestreamError(err, ERROR_MESSAGES);
      toast.error(errorMessage);
    } finally {
      setIsStopping(false);
    }
  }, [livestreamId, dispatch, fetchLivestream, ERROR_MESSAGES]);

  const handleDeleteLivestream = useCallback(async () => {
    if (!livestreamId) return;

    if (!window.confirm('Are you sure you want to delete this livestream? This action cannot be undone.')) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteLivestream(livestreamId);
      toast.success('Livestream deleted successfully');
      
      // Refresh livestreams list
      dispatch(fetchLivestreams());
    } catch (err) {
      const errorMessage = handleLivestreamError(err, ERROR_MESSAGES);
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  }, [livestreamId, dispatch, ERROR_MESSAGES]);

  const handleCreateLivestream = useCallback(async (livestreamData, onSuccess) => {
    try {
      const response = await createLivestream(livestreamData);
      
      if (!response || !response.id) {
        throw new Error('Failed to create livestream: Invalid response from server');
      }

      const savedStream = response;

      // Refresh the livestreams list
      dispatch(fetchLivestreams());

      // Handle payment if needed - but don't open new tab
      if (livestreamData.requiresPayment && user) {
        const paymentReq = {
          userId: user.id,
          discountCode: livestreamData.discountCode || null,
          successUrl: `${window.location.origin}/livestream/watch/${savedStream.id}`,
          cancelUrl: `${window.location.origin}/payment-cancel`
        };

        const checkoutSession = await createCheckoutSession(savedStream.id, paymentReq);

        if (!checkoutSession?.url) {
          throw new Error('Failed to create checkout session: No URL returned');
        }

        // Instead of opening new tab, redirect to checkout in same window
        window.location.href = checkoutSession.url;
        return; // Exit early since we're redirecting
      }

      if (onSuccess) {
        onSuccess(savedStream);
      }

      return savedStream;
    } catch (err) {
      const errorMessage = handleLivestreamError(err, ERROR_MESSAGES);
      throw new Error(errorMessage);
    }
  }, [user, dispatch, ERROR_MESSAGES]);

  // Enhanced scheduling flow that aligns with backend architecture
  const handleScheduleLivestream = useCallback(async (formData, courts, onSuccess) => {
    try {
      setIsScheduling(true);
      
      // Format data according to backend requirements
      const scheduleData = livestreamScheduleService.formatScheduleData(formData, courts);
      
      // Process complete scheduling flow with owner payment handling
      const result = await livestreamScheduleService.completeScheduleFlow(scheduleData, user);
      
      if (!result.success) {
        throw new Error(result.error);
      }

      const scheduledLivestream = result.livestream;

      // Refresh the livestreams list
      dispatch(fetchLivestreams());

      // Handle owner payment if required
      if (result.paymentRequired && result.paymentUrl) {
        toast.success('Livestream scheduled! Owner payment required to enable streaming.');
        
        // Open payment in new tab for better UX
        window.open(result.paymentUrl, '_blank', 'noopener');
        
        if (onSuccess) {
          onSuccess(scheduledLivestream, { paymentRequired: true });
        }
      } else {
        toast.success(result.message);
        
        if (onSuccess) {
          onSuccess(scheduledLivestream);
        }
      }

      return scheduledLivestream;
    } catch (err) {
      const errorMessage = handleLivestreamError(err, ERROR_MESSAGES);
      throw new Error(errorMessage);
    } finally {
      setIsScheduling(false);
    }
  }, [user, dispatch, ERROR_MESSAGES]);

  // Initial fetch
  useEffect(() => {
    if (livestreamId && !initialLivestream) {
      fetchLivestream();
    }
  }, [livestreamId, initialLivestream, fetchLivestream]);

  return {
    livestream,
    isLoading,
    error,
    isJoining,
    isStopping,
    isDeleting,
    isScheduling,
    fetchLivestream,
    handleJoinStream,
    handleStopStream,
    handleDeleteLivestream,
    handleCreateLivestream,
    handleScheduleLivestream, // Enhanced scheduling flow
    ERROR_MESSAGES
  };
}; 